/**
 * WorkshopBuilder Component - Main container for workshop management
 * 
 * This component follows the architectural pattern:
 * - Uses custom hook (useWorkshops) for all data/state management
 * - Composes smaller components for UI
 * - Manages modal state and delegates to child components
 * 
 * Architecture:
 * - hooks/useWorkshops.js: Data fetching and API calls
 * - components/workshops/WorkshopList.js: Workshop list panel
 * - components/workshops/WorkshopDetails.js: Details and tabs
 * - components/workshops/WorkshopModals.js: All modal dialogs
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Plus } from 'lucide-react';
import { useWorkshops } from '@/pages/special-access/hooks/useWorkshops';
import { 
  WorkshopList, 
  WorkshopDetails,
  PendingInvitations,
  WorkshopModal,
  CollaboratorModal,
  ModuleModal,
  FolderModal,
  ContentModal
} from '@/components/workshops';

// Default form states
const WORKSHOP_FORM_DEFAULT = {
  title: '', description: '', price: '', category_id: '',
  access_type: 'lifetime', access_duration_days: 30, image_url: '', preview_video_url: '', tags: [],
  membership_tier: 'any', prerequisite_workshops: [], prerequisite_modules: []
};

const MODULE_FORM_DEFAULT = { title: '', description: '', order: 0 };
const FOLDER_FORM_DEFAULT = { module_id: '', title: '', description: '', order: 0 };
const CONTENT_FORM_DEFAULT = { folder_id: '', module_id: '', target: '', title: '', content_type: 'video', file_url: '', content_data: {}, order: 0 };
const COLLAB_FORM_DEFAULT = { user_id: '', percentage: 10 };

const WorkshopBuilder = ({ user }) => {
  const workshopHook = useWorkshops(user);
  const {
    workshops, categories, practitioners, availableTags, prerequisiteOptions, loading, selectedWorkshop,
    fetchWorkshops, fetchCategories, fetchPractitioners, fetchTags, fetchPrerequisiteOptions, fetchWorkshopDetails,
    createWorkshop, updateWorkshop, deleteWorkshop, submitForApproval,
    deactivateWorkshop, reactivateWorkshop,
    inviteCollaborator, respondToCollaboration, approveFinal,
    createModule, updateModule, deleteModule,
    createFolder, updateFolder, deleteFolder,
    createContent, updateContent, deleteContent,
    createCategory,
    uploadCoverImage, uploadContentFile,
    getPendingInvitations
  } = workshopHook;
  
  // Modal states
  const [showWorkshopModal, setShowWorkshopModal] = useState(false);
  const [showCollaboratorModal, setShowCollaboratorModal] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  
  // Form states
  const [workshopForm, setWorkshopForm] = useState(WORKSHOP_FORM_DEFAULT);
  const [moduleForm, setModuleForm] = useState(MODULE_FORM_DEFAULT);
  const [folderForm, setFolderForm] = useState(FOLDER_FORM_DEFAULT);
  const [contentForm, setContentForm] = useState(CONTENT_FORM_DEFAULT);
  const [collabForm, setCollabForm] = useState(COLLAB_FORM_DEFAULT);
  
  // Edit tracking
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editingContent, setEditingContent] = useState(null);
  
  // Initial data fetch
  useEffect(() => {
    fetchWorkshops();
    fetchCategories();
    fetchPractitioners();
    fetchTags();
    fetchPrerequisiteOptions();
  }, [fetchWorkshops, fetchCategories, fetchPractitioners, fetchTags, fetchPrerequisiteOptions]);
  
  // Computed values
  const pendingInvitations = getPendingInvitations();
  const isCreator = selectedWorkshop?.creator_id === user.id;
  // Allow editing for draft, approved, and inactive workshops 
  // Explicitly exclude: pending_admin_approval, pending_review, rejected
  const editableStatuses = ['draft', 'approved', 'live', 'published', 'inactive'];
  const isPendingApproval = selectedWorkshop?.status === 'pending_admin_approval' || 
                            selectedWorkshop?.status === 'pending_review';
  const canEdit = !isPendingApproval && 
    editableStatuses.includes(selectedWorkshop?.status) && 
    (isCreator || selectedWorkshop?.collaborators?.some(
      c => c.user_id === user.id && c.status === 'accepted'
    ));
  const myCollab = selectedWorkshop?.collaborators?.find(c => c.user_id === user.id);
  
  // Get all folders for content modal
  const allFolders = selectedWorkshop?.modules?.flatMap(m => 
    m.folders?.map(f => ({ ...f, moduleName: m.title })) || []
  ) || [];
  
  // ===== WORKSHOP HANDLERS =====
  
  const handleSelectWorkshop = async (workshopId) => {
    await fetchWorkshopDetails(workshopId);
  };
  
  const handleCreateWorkshop = () => {
    setEditingWorkshop(null);
    setWorkshopForm(WORKSHOP_FORM_DEFAULT);
    setShowWorkshopModal(true);
  };
  
  const handleEditWorkshop = () => {
    setEditingWorkshop(selectedWorkshop);
    setWorkshopForm({
      title: selectedWorkshop.title,
      description: selectedWorkshop.description,
      price: selectedWorkshop.price.toString(),
      platform_fee_percent: selectedWorkshop.platform_fee_percent ?? null,
      category_id: selectedWorkshop.category_id || '',
      access_type: selectedWorkshop.access_type || 'lifetime',
      access_duration_days: selectedWorkshop.access_duration_days || 30,
      image_url: selectedWorkshop.image_url || '',
      preview_video_url: selectedWorkshop.preview_video_url || '',
      tags: selectedWorkshop.tags || [],
      membership_tier: selectedWorkshop.membership_tier || 'any',
      prerequisite_workshops: selectedWorkshop.prerequisite_workshops || [],
      prerequisite_modules: selectedWorkshop.prerequisite_modules || []
    });
    setShowWorkshopModal(true);
  };
  
  const handleSaveWorkshop = async () => {
    const data = {
      title: workshopForm.title,
      description: workshopForm.description,
      price: parseFloat(workshopForm.price),
      category_id: workshopForm.category_id || null,
      access_type: workshopForm.access_type,
      access_duration_days: workshopForm.access_type === 'time_limited'
        ? workshopForm.access_duration_days : null,
      image_url: workshopForm.image_url || null,
      preview_video_url: (workshopForm.preview_video_url || '').trim() || null,
      tags: workshopForm.tags || [],
      membership_tier: workshopForm.membership_tier || 'any',
      prerequisite_workshops: workshopForm.prerequisite_workshops || [],
      prerequisite_modules: workshopForm.prerequisite_modules || []
    };
    
    const success = editingWorkshop 
      ? await updateWorkshop(editingWorkshop.id, data)
      : await createWorkshop(data);
    
    if (success) {
      setShowWorkshopModal(false);
      setEditingWorkshop(null);
      // Refresh tags after creating/updating (new tags might have been added)
      fetchTags();
    }
  };
  
  // Cover image upload handler
  const handleCoverUpload = async (file) => {
    if (editingWorkshop) {
      await uploadCoverImage(editingWorkshop.id, file);
    }
  };
  
  // ===== MODULE HANDLERS =====
  
  const handleAddModule = () => {
    setEditingModule(null);
    const currentModules = selectedWorkshop?.modules || [];
    setModuleForm({ ...MODULE_FORM_DEFAULT, order: currentModules.length });
    setShowModuleModal(true);
  };
  
  const handleEditModule = (module) => {
    setEditingModule(module);
    setModuleForm({ title: module.title, description: module.description || '', order: module.order });
    setShowModuleModal(true);
  };
  
  const handleSaveModule = async () => {
    const success = editingModule
      ? await updateModule(editingModule.id, moduleForm)
      : await createModule(selectedWorkshop.id, moduleForm);
    
    if (success) {
      setShowModuleModal(false);
      setEditingModule(null);
    }
  };
  
  // ===== FOLDER HANDLERS =====
  
  const handleAddFolder = () => {
    setEditingFolder(null);
    const firstModuleId = selectedWorkshop?.modules?.[0]?.id || '';
    setFolderForm({ ...FOLDER_FORM_DEFAULT, module_id: firstModuleId });
    setShowFolderModal(true);
  };
  
  const handleEditFolder = (folder, moduleId) => {
    setEditingFolder(folder);
    setFolderForm({ 
      module_id: moduleId,
      title: folder.title, 
      description: folder.description || '', 
      order: folder.order 
    });
    setShowFolderModal(true);
  };
  
  const handleSaveFolder = async () => {
    const success = editingFolder
      ? await updateFolder(editingFolder.id, folderForm)
      : await createFolder(folderForm.module_id, folderForm);
    
    if (success) {
      setShowFolderModal(false);
      setEditingFolder(null);
    }
  };
  
  // ===== CONTENT HANDLERS =====
  
  const handleAddContent = () => {
    setEditingContent(null);
    // Default to first module (direct) or first folder
    const firstModuleId = selectedWorkshop?.modules?.[0]?.id || '';
    const defaultTarget = firstModuleId ? `module_${firstModuleId}` : (allFolders[0]?.id || '');
    setContentForm({ 
      ...CONTENT_FORM_DEFAULT, 
      target: defaultTarget,
      module_id: firstModuleId,
      folder_id: ''
    });
    setShowContentModal(true);
  };
  
  const handleEditContent = (content, folderId, moduleId = null) => {
    setEditingContent(content);
    setContentForm({
      folder_id: folderId || '',
      module_id: moduleId || content.module_id || '',
      target: folderId || (moduleId ? `module_${moduleId}` : ''),
      title: content.title,
      content_type: content.content_type,
      file_url: content.file_url || '',
      content_data: content.content_data || {},
      order: content.order
    });
    setShowContentModal(true);
  };
  
  const handleSaveContent = async () => {
    // Determine if targeting module directly or folder
    const isModuleTarget = contentForm.target?.startsWith('module_');
    const targetId = isModuleTarget 
      ? contentForm.target.replace('module_', '')
      : contentForm.folder_id;
    
    const contentData = {
      title: contentForm.title,
      content_type: contentForm.content_type,
      file_url: contentForm.file_url,
      content_data: contentForm.content_data,
      order: contentForm.order
    };
    
    const success = editingContent
      ? await updateContent(editingContent.id, contentData)
      : await createContent(targetId, contentData, isModuleTarget);
    
    if (success) {
      setShowContentModal(false);
      setEditingContent(null);
    }
  };
  
  // ===== COLLABORATOR HANDLERS =====
  
  const handleInviteCollaborator = () => {
    setCollabForm(COLLAB_FORM_DEFAULT);
    setShowCollaboratorModal(true);
  };
  
  const handleSaveCollaborator = async () => {
    const success = await inviteCollaborator(selectedWorkshop.id, {
      user_id: collabForm.user_id,
      income_percentage: collabForm.percentage
    });
    
    if (success) {
      setShowCollaboratorModal(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">My Workshops</h3>
          <p className="text-sm text-gray-400">Create and manage your workshops</p>
        </div>
        <Button onClick={handleCreateWorkshop} className="bg-cyan-600 hover:bg-cyan-700">
          <Plus className="w-4 h-4 mr-2" /> Create Workshop
        </Button>
      </div>
      
      {/* Pending Invitations */}
      <PendingInvitations
        invitations={pendingInvitations}
        userId={user.id}
        onRespond={respondToCollaboration}
      />
      
      {/* Main Layout */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Workshop List */}
        <div className="lg:col-span-1">
          <WorkshopList
            workshops={workshops}
            selectedWorkshop={selectedWorkshop}
            onSelectWorkshop={handleSelectWorkshop}
            userId={user.id}
          />
        </div>
        
        {/* Workshop Details */}
        <div className="lg:col-span-2">
          <WorkshopDetails
            workshop={selectedWorkshop}
            user={user}
            canEdit={canEdit}
            isCreator={isCreator}
            myCollab={myCollab}
            onEdit={handleEditWorkshop}
            onDelete={deleteWorkshop}
            onSubmitForApproval={submitForApproval}
            onApproveFinal={approveFinal}
            onInviteCollaborator={handleInviteCollaborator}
            onDeactivate={deactivateWorkshop}
            onReactivate={reactivateWorkshop}
            onAddModule={handleAddModule}
            onEditModule={handleEditModule}
            onDeleteModule={deleteModule}
            onAddFolder={handleAddFolder}
            onEditFolder={handleEditFolder}
            onDeleteFolder={deleteFolder}
            onAddContent={handleAddContent}
            onEditContent={handleEditContent}
            onDeleteContent={deleteContent}
          />
        </div>
      </div>
      
      {/* Modals */}
      <WorkshopModal
        open={showWorkshopModal}
        onOpenChange={setShowWorkshopModal}
        form={workshopForm}
        setForm={setWorkshopForm}
        categories={categories}
        availableTags={availableTags}
        isEditing={!!editingWorkshop}
        onSave={handleSaveWorkshop}
        onCoverUpload={handleCoverUpload}
        onCreateCategory={createCategory}
        prerequisiteOptions={prerequisiteOptions}
      />
      
      <CollaboratorModal
        open={showCollaboratorModal}
        onOpenChange={setShowCollaboratorModal}
        form={collabForm}
        setForm={setCollabForm}
        practitioners={practitioners}
        onSave={handleSaveCollaborator}
      />
      
      <ModuleModal
        open={showModuleModal}
        onOpenChange={setShowModuleModal}
        form={moduleForm}
        setForm={setModuleForm}
        isEditing={!!editingModule}
        onSave={handleSaveModule}
      />
      
      <FolderModal
        open={showFolderModal}
        onOpenChange={setShowFolderModal}
        form={folderForm}
        setForm={setFolderForm}
        modules={selectedWorkshop?.modules}
        isEditing={!!editingFolder}
        onSave={handleSaveFolder}
      />
      
      <ContentModal
        open={showContentModal}
        onOpenChange={setShowContentModal}
        form={contentForm}
        setForm={setContentForm}
        folders={allFolders}
        modules={selectedWorkshop?.modules}
        isEditing={!!editingContent}
        onSave={handleSaveContent}
        workshopId={selectedWorkshop?.id}
      />
    </div>
  );
};

export default WorkshopBuilder;
